// Field messages used to carry the whole trainer (about 8 KB late in the game) on every tick with a kill: close
// to half of all bytes the server sent. Now they carry what changed since the last one: top-level keys set whole
// or removed, and for a key holding a plain object (the Bestiary's per-species counts, quests, stats) only its
// changed entries. The server keeps a 32-bit hash per entry, never the entries, and sends the whole trainer
// again every TRAINER_FULL_MS, after a sync and when a socket joins, so a client can never drift for long.

/** What changed in the trainer since the last field message that carried it. */
export interface TrainerPatch {
  /** Top-level keys, set whole. */
  set?: Record<string, unknown>;
  /** Top-level keys removed. */
  del?: string[];
  /** Keys holding a plain object: the entries set or removed. */
  sub?: Record<string, { set?: Record<string, unknown>; del?: string[] }>;
}

/** The whole trainer goes out at least this often (on a tick with something to report). */
export const TRAINER_FULL_MS = 60_000;

type Rec = Record<string, unknown>;
const isRecord = (v: unknown): v is Rec => typeof v === 'object' && v !== null && !Array.isArray(v);

const P = 0x01000193;
const F64 = new Float64Array(1), U32 = new Uint32Array(F64.buffer);
/**
 * A 32-bit FNV-style hash of a value as JSON sees it (undefined entries left out), walked in place: no string is
 * built, which made a field message's diff cost more than the whole trainer's JSON. Each step is a bijection on
 * the hash, so a value that differs in one number or one character never hashes the same.
 */
function hv(v: unknown, h: number): number {
  if (typeof v === 'number') {
    if ((v | 0) === v) return Math.imul(Math.imul(h ^ 1, P) ^ v, P);
    F64[0] = v;
    return Math.imul(Math.imul(Math.imul(h ^ 2, P) ^ U32[0], P) ^ U32[1], P);
  }
  if (typeof v === 'string') {
    h = Math.imul(h ^ 3, P);
    for (let i = 0; i < v.length; i++) h = Math.imul(h ^ v.charCodeAt(i), P);
    return Math.imul(h ^ v.length, P);
  }
  if (typeof v === 'boolean') return Math.imul(h ^ (v ? 4 : 5), P);
  if (v === null || v === undefined) return Math.imul(h ^ 6, P);
  if (Array.isArray(v)) {
    h = Math.imul(h ^ 7, P);
    for (let i = 0; i < v.length; i++) h = hv(v[i], h);
    return Math.imul(h ^ v.length, P);
  }
  if (typeof v === 'object') {
    h = Math.imul(h ^ 8, P);
    for (const k in v) {
      const x = (v as Rec)[k];
      if (x !== undefined) h = hv(x, hv(k, h));
    }
    return Math.imul(h ^ 9, P);
  }
  return Math.imul(h ^ 10, P);
}
const hashOf = (v: unknown) => hv(v, 0x811c9dc5 | 0);

/** A key written as data; never '__proto__', which would set the prototype instead. */
function put(o: Rec, k: string, v: unknown) { if (k !== '__proto__') o[k] = v; }

/** The client's side: the trainer with a patch applied, as a new object (unchanged keys keep their objects). */
export function applyTrainerPatch<T extends object>(t: T, p: TrainerPatch): T {
  const out: Rec = { ...(t as Rec) };
  for (const k of p.del ?? []) delete out[k];
  if (p.set) for (const k of Object.keys(p.set)) put(out, k, p.set[k]);
  if (p.sub) for (const k of Object.keys(p.sub)) {
    const s = p.sub[k], was = out[k];
    const o: Rec = isRecord(was) ? { ...was } : {};
    for (const sk of s.del ?? []) delete o[sk];
    if (s.set) for (const sk of Object.keys(s.set)) put(o, sk, s.set[sk]);
    put(out, k, o);
  }
  return out as T;
}

/** The server's side, one per online player: what the sockets were last sent, as hashes. */
export class TrainerDiff {
  /** Top-level key -> hash of its value, or (a plain object) entry -> hash of the entry. Null: send the whole trainer next. */
  private seen: Map<string, number | Map<string, number>> | null = null;
  private fullAt = 0;

  /** Forget what was sent, so the next field message carries the whole trainer (a socket joined). */
  reset() { this.seen = null; }

  /** Every socket was just sent the whole trainer (a sync): patches start from here. */
  sent(t: object, now: number) { this.seen = null; this.next(t, now); }

  /** What a field message carries: the whole trainer when due, else what changed (null when nothing did). */
  next(t: object, now: number): { trainer: object } | { tp: TrainerPatch } | null {
    const full = !this.seen || now - this.fullAt >= TRAINER_FULL_MS;
    if (full) { this.seen = new Map(); this.fullAt = now; }
    const seen = this.seen!;
    const tr = t as Rec;
    const p: TrainerPatch = {};
    let keys = 0;
    for (const k of Object.keys(tr)) {
      const v = tr[k];
      if (v === undefined) continue;
      keys++;
      const was = seen.get(k);
      if (!isRecord(v)) {
        const h = hashOf(v);
        if (was !== h) { seen.set(k, h); if (!full) (p.set ??= {})[k] = v; }
        continue;
      }
      // A plain object: entry by entry. One that was not a plain object last time goes out whole.
      const fresh = !(was instanceof Map);
      const m = fresh ? new Map<string, number>() : was;
      if (fresh) { seen.set(k, m); if (!full) (p.set ??= {})[k] = v; }
      const quiet = full || fresh;
      let set: Rec | undefined, del: string[] | undefined, entries = 0;
      for (const sk of Object.keys(v)) {
        const sv = v[sk];
        if (sv === undefined) continue;
        entries++;
        const h = hashOf(sv);
        if (m.get(sk) !== h) { m.set(sk, h); if (!quiet) (set ??= {})[sk] = sv; }
      }
      if (m.size > entries) {
        for (const sk of [...m.keys()]) if (v[sk] === undefined || !Object.hasOwn(v, sk)) { m.delete(sk); if (!quiet) (del ??= []).push(sk); }
      }
      if (set || del) (p.sub ??= {})[k] = { ...(set ? { set } : {}), ...(del ? { del } : {}) };
    }
    if (seen.size > keys) {
      for (const k of [...seen.keys()]) if (tr[k] === undefined || !Object.hasOwn(tr, k)) { seen.delete(k); if (!full) (p.del ??= []).push(k); }
    }
    if (full) return { trainer: t };
    return p.set || p.del || p.sub ? { tp: p } : null;
  }
}
